import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { SafeImage } from "./SafeImage";

export const Lightbox = ({ cottage, index, onIndex, onClose }) => {
  const { t } = useLang();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const touchX = useRef(null);
  const photos = cottage.photos;
  const total = photos.length;
  const photo = photos[index];
  const caption = t.captions[photo.caption];
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Tab") {
        const buttons = Array.from(dialogRef.current?.querySelectorAll("button:not(:disabled)") || []);
        if (buttons.length) {
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (e.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
            e.preventDefault();
            first.focus();
          }
        }
      }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && hasNext) onIndex(index + 1);
      if (e.key === "ArrowLeft" && hasPrev) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasNext, hasPrev, onClose, onIndex]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx < -45 && hasNext) onIndex(index + 1);
    if (dx > 45 && hasPrev) onIndex(index - 1);
  };

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      ref={dialogRef}
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${t.lightbox.label} – ${cottage.name}`}
      onClick={onBackdrop}
      data-testid="lightbox"
      data-cottage={cottage.key}
    >
      <div className="lightbox__top">
        <span className="lightbox__name">{cottage.name}</span>
        <span className="lightbox__counter" data-testid="lightbox-counter">
          {index + 1} / {total}
        </span>
        <button ref={closeRef} type="button" className="lightbox__btn" onClick={onClose} aria-label={t.lightbox.close} data-testid="lightbox-close-button">
          <X size={22} strokeWidth={1.4} />
        </button>
      </div>
      <div
        className="lightbox__stage"
        onClick={onBackdrop}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={onTouchEnd}
      >
        <button type="button" className="lightbox__btn lightbox__nav lightbox__nav--prev" onClick={() => onIndex(index - 1)} disabled={!hasPrev} aria-label={t.lightbox.prev} data-testid="lightbox-prev-button">
          <ChevronLeft size={26} strokeWidth={1.3} />
        </button>
        <figure className="lightbox__figure" key={photo.id}>
          <SafeImage className="lightbox__img" src={photo.src} alt={`${cottage.name} – ${caption}`} testId="lightbox-image" data-photo-id={photo.id} />
          <figcaption className="lightbox__caption" data-testid="lightbox-caption">{caption}</figcaption>
        </figure>
        <button type="button" className="lightbox__btn lightbox__nav lightbox__nav--next" onClick={() => onIndex(index + 1)} disabled={!hasNext} aria-label={t.lightbox.next} data-testid="lightbox-next-button">
          <ChevronRight size={26} strokeWidth={1.3} />
        </button>
      </div>
    </div>,
    document.body
  );
};
